/**
 * Typed errors thrown by the apiFetch family (services/api/core.ts).
 *
 * Kept in its own module rather than inside core.ts so a consumer can branch
 * on an error without importing core itself: several suites mock
 * `services/api/core` wholesale (IncidentResponseWorkflow.test.tsx among
 * them), and a predicate living there would vanish under those mocks.
 *
 * THIS is the Error+status class for the whole app. `aiApplicationsService`
 * hand-rolls its own fetch rather than going through `apiFetch`, and used to
 * carry a near-identical local `ApiError` for the same job; it now throws this
 * one. Anything else that grows a fetch wrapper should do the same rather than
 * write a third — two of these was already enough for the next reader to find
 * the wrong one first.
 *
 * Not to be confused with the `ApiError` TYPE in `types/api.ts`, which is the
 * shape of an error response BODY, not a thrown value.
 */

/**
 * Error thrown by the apiFetch family for a non-2xx response whose body
 * carried a backend message. It IS an `Error` with the same `message` the
 * plain `Error` used to carry, so every existing `instanceof Error` /
 * `.message` caller is unaffected; it only adds the HTTP `status` and the
 * backend envelope's machine-readable `code` (`{code, message, details}`)
 * for callers that need to branch on a specific failure — e.g. the
 * process-kill approve 409 `target_terminated` (PENG-14004) — without
 * string-matching the human-readable message.
 *
 * `name` is deliberately left as the inherited `'Error'`: callers elsewhere
 * branch on `error.name` (core.ts's own `'AbortError'` check among them), and a
 * new name would be an observable change for code that never asked for one.
 */
// @purpose The app's Error+status class for a failed API call — adds the HTTP `status` and the backend envelope's machine-readable `code` to a normal Error. Throw this from any fetch wrapper rather than writing another one.
export class ApiFetchError extends Error {
  readonly status: number
  /** Backend error code, when the error body carried one. */
  readonly code: string | undefined
  /**
   * The whole parsed error body, unchecked. For endpoints whose error carries
   * more than a message, such as the GCP connection 409's `conflicts` list.
   * A caller reads it through its own type guard.
   */
  readonly body: unknown
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.body = body
  }
}

/** True when a thrown value is an `ApiFetchError` whose backend `code` matches. */
// @purpose Predicate for branching on a backend error code (e.g. the process-kill 409 `target_terminated`) without string-matching a human-readable message.
export const hasApiErrorCode = (error: unknown, code: string): boolean =>
  error instanceof ApiFetchError && error.code === code

/**
 * True when a thrown value is a cancellation — an aborted `fetch`, or core.ts
 * rethrowing one for an external (non-timeout) abort.
 *
 * Checks `name` WITHOUT an `instanceof` narrowing on purpose. `fetch` rejects
 * with a `DOMException`, which is only an `Error` subclass in real browsers —
 * under jsdom it is not, so the `error instanceof Error && ...` spelling used
 * elsewhere in the app silently fails to match a cancellation in tests, and
 * the abort gets handled as a real failure.
 */
// @purpose Predicate for "this rejection was a cancellation, not a failure" — use it before logging/handling an error from any abortable fetch, instead of hand-rolling an `instanceof Error && name === 'AbortError'` check that misses jsdom's DOMException.
export const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError'
